/**
 * `growth/rewards` — rewarded redemption, **local-first**.
 *
 * Catalog rules (§5 of the strategy), applied here and not in each app:
 *   - the reward is **temporary or consumable**; permanent things are premium →
 *     every grant carries `expiresAt` or is a consumable that gets spent;
 *   - it is granted ONLY if the provider returned `reward` (watching half an ad does not
 *     pay);
 *   - it is never rewarded with user data or security/privacy
 *     features (that is catalog policy: the kit only transports
 *     what the app declares in its placements registry).
 *
 * The store is local (works offline and for guests). If a reward
 * touches the backend (raising a real quota), the app re-verifies it server-side
 * — this store is the client's optimistic view.
 */

import type { AdCapsStorage } from "./caps";
import type { AdReward } from "./types";

/** Canonical key of the active rewards store. */
export const AD_REWARDS_KEY = "ads.rewards";

/** A granted and live reward. */
export interface AdRewardGrant {
  type: string;
  amount: number;
  grantedAt: number;
  /** Epoch ms of expiry. Absent ⇒ consumable (gets spent, does not expire). */
  expiresAt?: number;
}

const HOUR_MS = 3_600_000;

function isGrant(v: unknown): v is AdRewardGrant {
  if (typeof v !== "object" || v === null) {
    return false;
  }
  const g = v as Partial<AdRewardGrant>;
  return typeof g.type === "string" && typeof g.amount === "number";
}

/**
 * Active rewards store. Domain-agnostic: the app decides what
 * each `type` MEANS (see `growth/domain.ts` in each clone).
 */
export class AdRewardsStore {
  private grants: AdRewardGrant[];

  constructor(
    private readonly storage: AdCapsStorage,
    private readonly scope = "device",
  ) {
    this.grants = this.load();
  }

  private get key(): string {
    return `${AD_REWARDS_KEY}.${this.scope}`;
  }

  private load(): AdRewardGrant[] {
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(isGrant) : [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    try {
      this.storage.setItem(this.key, JSON.stringify(this.grants));
    } catch {
      // The in-memory grant stays alive this session even if it cannot be saved.
    }
  }

  /**
   * Grants a reward. Returns the created grant, or `null` if the provider
   * did not confirm a reward (`reward` absent ⇒ the ad was not completed).
   */
  grant(reward: AdReward | undefined, now: number = Date.now()): AdRewardGrant | null {
    if (!reward || reward.amount <= 0) {
      return null;
    }
    const grant: AdRewardGrant = {
      type: reward.type,
      amount: reward.amount,
      grantedAt: now,
      ...(reward.durationHours ? { expiresAt: now + reward.durationHours * HOUR_MS } : {}),
    };
    this.grants = [...this.active(now), grant];
    this.persist();
    return grant;
  }

  /** Live grants (expired ones are pruned at read time). */
  active(now: number = Date.now()): AdRewardGrant[] {
    const alive = this.grants.filter((g) => g.expiresAt === undefined || g.expiresAt > now);
    if (alive.length !== this.grants.length) {
      this.grants = alive;
      this.persist();
    }
    return [...alive];
  }

  /** Total live amount of a type (e.g. how many `streak-freeze` there are). */
  amountOf(type: string, now: number = Date.now()): number {
    return this.active(now)
      .filter((g) => g.type === type)
      .reduce((sum, g) => sum + g.amount, 0);
  }

  /** Is there any live temporary unlock of this type? */
  hasUnlock(type: string, now: number = Date.now()): boolean {
    return this.amountOf(type, now) > 0;
  }

  /**
   * Spends `amount` units of a consumable (e.g. using a streak-freeze).
   * Returns how many were actually spent.
   */
  consume(type: string, amount = 1, now: number = Date.now()): number {
    let remaining = amount;
    const next: AdRewardGrant[] = [];
    for (const g of this.active(now)) {
      if (g.type !== type || remaining <= 0) {
        next.push(g);
        continue;
      }
      const taken = Math.min(g.amount, remaining);
      remaining -= taken;
      if (g.amount > taken) {
        next.push({ ...g, amount: g.amount - taken });
      }
    }
    this.grants = next;
    this.persist();
    return amount - remaining;
  }

  /** Post-premium purchase: ad rewards stop making sense. */
  clear(): void {
    this.grants = [];
    try {
      this.storage.removeItem(this.key);
    } catch {
      // in-memory state already clean
    }
  }
}
